import React, { useState, useEffect } from 'react';
import { firestore } from '../../firebase/config';
import emailService from '../../services/emailService';
import './AppointmentEditModal.css';

const AppointmentEditModal = ({ appointment, onClose, onUpdate, userRole = 'client' }) => {
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    notes: appointment.notes || ''
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

   // Stocker les données originales du rendez-vous pour comparaison
  const [originalAppointment] = useState({
    date: appointment.date,
    time: appointment.time,
    formattedDate: appointment.formattedDate,
    notes: appointment.notes || ''
  });

  // Initialiser le formulaire avec les données actuelles
  useEffect(() => {
    if (appointment) {
      setFormData({
        date: appointment.date,
        time: appointment.time,
        notes: appointment.notes || ''
      });
      loadAvailableSlots(appointment.date);
    }
  }, [appointment]);

  // Vérifier si on peut modifier le rendez-vous (24h avant)
  const canModifyAppointment = () => {
    const appointmentDateTime = new Date(`${appointment.date}T${appointment.time}`);
    const now = new Date();
    const diffHours = (appointmentDateTime - now) / (1000 * 60 * 60);
    return diffHours > 24;
  };

  // Charger les créneaux disponibles pour une date donnée
 const loadAvailableSlots = async (selectedDate) => {
  if (!selectedDate || !appointment.practitionerId) return;

  try {
    setLoading(true);

    const [year, month, day] = selectedDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    const dayOfWeek = localDate.getDay();
    const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    let availability = null;

    // 1. Chercher disponibilité spécifique
    const specificSnapshot = await firestore
      .collection('availability')
      .where('practitionerId', '==', appointment.practitionerId)
      .where('type', '==', 'specific')
      .where('date', '==', selectedDate)
      .limit(1)
      .get();

    if (!specificSnapshot.empty) {
      availability = specificSnapshot.docs[0].data();
    } else {
      // 2. Sinon, chercher la disponibilité par défaut
      const defaultSnapshot = await firestore
        .collection('availability')
        .where('practitionerId', '==', appointment.practitionerId)
        .where('type', '==', 'default')
        .where('day', '==', adjustedDayOfWeek)
        .limit(1)
        .get();

      if (!defaultSnapshot.empty) {
        availability = defaultSnapshot.docs[0].data();
      }
    }

    // 3. Si aucune dispo ou jour non travaillé
    if (!availability || !availability.isWorkingDay) {
      setAvailableSlots([]);
      return;
    }

    // 4. Vérifier le type de rendez-vous autorisé
    const isRemote = appointment.mode === 'remote';
    const isPhysical = appointment.mode === 'physical';

    if (
      (appointment.isRemote === true && !availability.allowsRemote) ||
      (appointment.isRemote === false && availability.remoteOnly)
    ) {
      setAvailableSlots([]);
      return;
    }

    // 5. Récupérer les RDV confirmés pour ce jour
    const appointmentsSnapshot = await firestore
      .collection('appointments')
      .where('practitionerId', '==', appointment.practitionerId)
      .where('date', '==', selectedDate)
      .where('status', '==', 'confirmed')
      .get();

    const bookedSlots = appointmentsSnapshot.docs
      .filter(doc => doc.id !== appointment.id)
      .map(doc => {
        const data = doc.data();
        return {
          start: convertTimeToMinutes(data.time),
          end: convertTimeToMinutes(data.time) + (data.duration || 60) + (data.restTime || 0)
        };
      });

    // 6. Générer les créneaux
    const slots = generateTimeSlots(
      availability.startTime,
      availability.endTime,
      availability.hasLunchBreak ? availability.lunchBreak?.start : null,
      availability.hasLunchBreak ? availability.lunchBreak?.end : null,
      appointment.duration,
      bookedSlots
    );

    setAvailableSlots(slots);
  } catch (err) {
    console.error('Erreur lors du chargement des créneaux:', err);
    setError('Impossible de charger les créneaux disponibles');
  } finally {
    setLoading(false);
  }
};


  // Générer les créneaux horaires disponibles
  const generateTimeSlots = (startTime, endTime, lunchStart, lunchEnd, duration, bookedSlots) => {
    const slots = [];
    const interval = 15; // Intervalle entre les créneaux
    
    const startMinutes = convertTimeToMinutes(startTime);
    const endMinutes = convertTimeToMinutes(endTime);
    const lunchStartMinutes = lunchStart ? convertTimeToMinutes(lunchStart) : null;
    const lunchEndMinutes = lunchEnd ? convertTimeToMinutes(lunchEnd) : null;

    for (let minutes = startMinutes; minutes <= endMinutes - duration; minutes += interval) {
      const slotEndMinutes = minutes + duration;
      
      // Vérifier si le créneau chevauche la pause déjeuner
      const isDuringLunch = lunchStartMinutes !== null &&
        ((minutes >= lunchStartMinutes && minutes < lunchEndMinutes) ||
          (slotEndMinutes > lunchStartMinutes && slotEndMinutes <= lunchEndMinutes) ||
          (minutes < lunchStartMinutes && slotEndMinutes > lunchEndMinutes));

      // Vérifier si le créneau chevauche un rendez-vous existant
      const isBooked = bookedSlots.some(slot => 
        minutes < slot.end && slotEndMinutes > slot.start
      );

      if (!isDuringLunch && !isBooked) {
        slots.push({
          time: minutesToTimeString(minutes),
          label: `${minutesToTimeString(minutes)} - ${minutesToTimeString(slotEndMinutes)}`
        });
      }
    }

    return slots;
  };

  // Convertir une heure en minutes
  const convertTimeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convertir des minutes en heure
  const minutesToTimeString = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Gérer le changement de date
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setFormData({ ...formData, date: newDate, time: '' });
    loadAvailableSlots(newDate);
  };

  // Gérer les autres changements
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Fonction pour détecter les changements
  const hasChanges = () => {
    return formData.date !== originalAppointment.date || 
           formData.time !== originalAppointment.time ||
           formData.notes !== originalAppointment.notes;
  };

  // Soumettre les modifications
const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canModifyAppointment()) {
      setError('Impossible de modifier un rendez-vous moins de 24h avant');
      return;
    }

    if (!hasChanges()) {
      setError('Aucune modification détectée');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const updateData = {
        date: formData.date,
        time: formData.time,
        formattedDate: new Date(formData.date).toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }),
        notes: formData.notes,
        updatedAt: new Date(),
        modifiedBy: userRole
      };

      // Mettre à jour le rendez-vous dans Firestore
      await firestore
        .collection('appointments')
        .doc(appointment.id)
        .update(updateData);

      // Préparer les données du rendez-vous modifié
      const updatedAppointment = { 
        ...appointment, 
        ...updateData,
        id: appointment.id 
      };

      // Envoyer les emails de notification de modification
      try {
        console.log('Envoi des emails de notification de modification...');
        
        // Envoyer un email au client
        await emailService.sendAppointmentModificationEmail(
          updatedAppointment, 
          'client', 
          userRole,
          originalAppointment
        );
        
        // Envoyer un email au praticien si c'est le client qui modifie
        if (userRole === 'client') {
          await emailService.sendAppointmentModificationEmail(
            updatedAppointment, 
            'practitioner', 
            userRole,
            originalAppointment
          );
        }
        
        console.log('Emails de modification envoyés avec succès');
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi des emails de modification:', emailError);
        // Ne pas bloquer la modification si l'email échoue, mais informer l'utilisateur
        setError('Le rendez-vous a été modifié mais l\'envoi des emails de notification a échoué.');
      }

      setSuccess(true);
      
      // Notifier le parent
      if (onUpdate) {
        onUpdate(updatedAppointment);
      }

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      setError('Erreur lors de la modification du rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  // Calculer la date minimale (aujourd'hui + 1 jour)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (!canModifyAppointment()) {
    return (
      <div className="appointment-edit-modal">
        <div className="appointment-edit-content">
          <div className="appointment-edit-header">
            <h3>Modification impossible</h3>
            <button className="close-modal" onClick={onClose}>&times;</button>
          </div>
          <div className="edit-restriction-message">
            <div className="restriction-icon">⚠️</div>
            <h4>Délai de modification dépassé</h4>
            <p>
              Les rendez-vous ne peuvent être modifiés qu'au minimum 24 heures avant l'heure prévue.
            </p>
            <p>
              Pour tout changement urgent, veuillez contacter directement le{' '}
              {userRole === 'client' ? 'praticien' : 'client'}.
            </p>
            <button className="btn btn-primary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="appointment-edit-modal">
      <div className="appointment-edit-content">
        <div className="appointment-edit-header">
          <h3>Modifier le rendez-vous</h3>
          <button className="close-modal" onClick={onClose}>&times;</button>
        </div>

        {success && (
          <div className="success-message">
            Rendez-vous modifié avec succès !  Les participants ont été notifiés par email.
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="appointment-edit-form">
          <div className="current-appointment">
            <h4>Rendez-vous actuel</h4>
            <div className="current-details">
              <span>{appointment.formattedDate} à {appointment.time}</span>
              <span>{appointment.serviceTitle}</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="date">Nouvelle date</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleDateChange}
              min={getMinDate()}
              className="form-control"
              required
            />
          </div>

          {formData.date && (
            <div className="form-group">
              <label htmlFor="time">Nouveau créneau</label>
              {loading ? (
                <div className="loading-slots">Chargement des créneaux...</div>
              ) : availableSlots.length === 0 ? (
                <div className="no-slots">Aucun créneau disponible pour cette date</div>
              ) : (
                <select
                  id="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  <option value="">Sélectionnez un créneau</option>
                  {availableSlots.map((slot, index) => (
                    <option key={index} value={slot.time}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {userRole === 'client' && (
            <div className="form-group">
              <label htmlFor="notes">Notes (optionnel)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-control"
                rows="3"
                placeholder="Raison de la modification ou informations supplémentaires"
              />
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-cancel" onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.date || !formData.time}
            >
              {loading ? 'Modification...' : 'Confirmer la modification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentEditModal;